import React from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AlertCircle } from 'lucide-react';

import { LLMModelInvocationProfileInput } from '../../../types/admin';

export interface ProviderNativeProfileFieldsProps {
  profile: LLMModelInvocationProfileInput;
  supportedBodyBuilderKeys: string[];
  supportedParserKeys: string[];
  onUpdate: (patch: Partial<LLMModelInvocationProfileInput>) => void;
}

export const ProviderNativeProfileFields: React.FC<ProviderNativeProfileFieldsProps> = ({
  profile,
  supportedBodyBuilderKeys,
  supportedParserKeys,
  onUpdate,
}) => {
  return (
    <Box className="space-y-3 rounded-lg border border-amber-300 bg-amber-50/40 p-3 dark:border-amber-800 dark:bg-amber-950/20">
      <Box className="flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
        <AlertCircle size={16} />
        <Typography variant="subtitle2" className="font-semibold">
          Provider-Native AI Gateway Configuration
        </Typography>
      </Box>
      <Box className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextField
          size="small"
          label="Provider Slug"
          value={profile.providerSlug ?? ''}
          onChange={(e) => onUpdate({ providerSlug: e.target.value })}
          placeholder="openai, anthropic, google-ai-studio"
          helperText="Target provider in Cloudflare AI Gateway"
          required
        />
        <TextField
          size="small"
          label="Endpoint Path"
          value={profile.endpointPath ?? ''}
          onChange={(e) => onUpdate({ endpointPath: e.target.value })}
          placeholder="chat/completions, v1/messages"
          helperText="Relative path under provider"
          required
        />
        <TextField
          size="small"
          label="Provider Model ID"
          value={profile.providerModelCode ?? ''}
          onChange={(e) => onUpdate({ providerModelCode: e.target.value ? e.target.value : null })}
          placeholder="claude-haiku-4-5, gpt-4.1"
          error={!profile.providerModelCode?.trim()}
          helperText={
            profile.providerModelCode?.trim()
              ? `Sent to the provider as "model"`
              : `Required for provider-native calls. Empty → backend strips "${profile.providerSlug || '{slug}'}/" from the catalog model code, which is often wrong (e.g. claude-haiku-4.5 vs claude-haiku-4-5).`
          }
        />
        <FormControl size="small" fullWidth required>
          <InputLabel>Body Builder Key</InputLabel>
          <Select
            label="Body Builder Key"
            value={profile.bodyBuilderKey ?? 'openai_chat_completions'}
            onChange={(e) => onUpdate({ bodyBuilderKey: e.target.value })}
          >
            {supportedBodyBuilderKeys.map((key) => (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Request serializer</FormHelperText>
        </FormControl>
        <FormControl size="small" fullWidth required>
          <InputLabel>Parser Key</InputLabel>
          <Select
            label="Parser Key"
            value={profile.parserKey ?? 'openai_chat_completions'}
            onChange={(e) => onUpdate({ parserKey: e.target.value })}
          >
            {supportedParserKeys.map((key) => (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Response deserializer</FormHelperText>
        </FormControl>
      </Box>
    </Box>
  );
};
